import {
  Avatar,
  Combobox,
  Group,
  InputBase,
  Menu,
  Pagination,
  Stack,
  Text,
  ThemeIcon,
  useCombobox
} from "@mantine/core";
import { useDebouncedState } from "@tanstack/react-pacer";
import { useQuery } from "@tanstack/react-query";
import {
  IconCalendar,
  IconHome,
  IconInfoCircle,
  IconPhone,
  IconSearch
} from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useRef, useState } from "react";
import LoadingPane from "./LoadingPane";
import { searchProspectsQuery, useApi } from "@/api";

export default function ProspectSearch() {
  const [inputValue, setInputValue] = useState("");
  const [query, setQuery] = useDebouncedState("", {
    wait: 100
  });
  const [page, setPage] = useState(1);
  const inputRef = useRef<HTMLInputElement>(null);
  const { secureAxios } = useApi();
  const { data, isFetching } = useQuery(
    searchProspectsQuery(secureAxios, query, page, {
      enabled: query.length > 2
    })
  );
  const hasResults = (data?.results.length ?? 0) > 0;
  const hasSearched =
    inputValue.length > 2 && data !== undefined && !isFetching;

  // the combobox should only open if there are results, or if a search is currently in progress, or if a search has been completed with no results
  const combobox = useCombobox({
    opened: inputValue.length > 2 && (hasResults || isFetching || hasSearched)
  });
  const navigate = useNavigate();

  return (
    <Group gap={4} wrap="nowrap" justify="center" style={{ flex: 1, minWidth: 0 }}>
      <Combobox width={500} store={combobox}>
        <Combobox.Target>
          <InputBase
            component="input"
            styles={{
              root: { flex: 1, minWidth: 170, maxWidth: 500 },
              input: {
                width: "100%",
                color: "optimaBlue.4",
                "&:placeholder": { color: "optimaBlue.4" }
              }
            }}
            placeholder="Search prospects..."
            ref={inputRef}
            onChange={(e) => {
              setInputValue(e.target.value);
              setQuery(e.target.value);
            }}
            c="optimaBlue.4"
            value={inputValue}
            rightSection={
              <ThemeIcon variant="white" c="optimaBlue.9" radius="xl">
                <IconSearch />
              </ThemeIcon>
            }
          />
        </Combobox.Target>
        <Combobox.Dropdown p={10}>
          {data?.results.map((result, i) => {
            const name = result.middleName
              ? `${result.firstName} ${result.middleName} ${result.lastName}`
              : `${result.firstName} ${result.lastName}`;
            const bottomBorderStyle =
              i === data.results.length - 1 ? "none" : "solid";
            return (
              <Combobox.Option
                key={result.id}
                value={result.id}
                onClick={() => {
                  setInputValue("");
                  setQuery("");
                  setPage(1);
                  void navigate({ to: `/prospects/${result.id}` });
                }}
                style={{
                  borderBottom: `1px ${bottomBorderStyle} var(--mantine-color-gray-3)`
                }}
                mb={5}
              >
                <Stack gap="xs">
                  <Group>
                    <Avatar size="sm" name={name} alt={name} />
                    <Text truncate size="md">
                      {name}
                    </Text>
                  </Group>
                  <Group justify="center">
                    <Group gap={0}>
                      <ThemeIcon c="optimaBlue.9" variant="white">
                        <IconCalendar />
                      </ThemeIcon>
                      <Text size="sm" pl={0}>
                        {result.birthdate
                          ? DateTime.fromSQL(result.birthdate).toFormat(
                              "yyyy-MM-dd"
                            )
                          : "Not Specified"}
                      </Text>
                    </Group>
                    <Group gap={0}>
                      <ThemeIcon c="optimaBlue.9" variant="white">
                        <IconPhone />
                      </ThemeIcon>
                      <Text size="sm">
                        {result.cellPhone == ""
                          ? "Not Specified"
                          : result.cellPhone}
                      </Text>
                    </Group>
                    <Group gap={0}>
                      <ThemeIcon c="optimaBlue.9" variant="white">
                        <IconHome />
                      </ThemeIcon>
                      <Text size="sm">
                        {result.homePhone == ""
                          ? "Not Specified"
                          : result.homePhone}
                      </Text>
                    </Group>
                  </Group>
                </Stack>
              </Combobox.Option>
            );
          })}
          {isFetching && !data?.results && <LoadingPane />}
          {hasSearched && !hasResults && (
            <Text c="dimmed" ta="center" py="md" size="sm">
              No prospects found for "{inputValue}"
            </Text>
          )}
          <Pagination
            value={page}
            onChange={setPage}
            total={Math.ceil((data?.totalResultCount ?? 0) / 5)}
            color="optimaBlue.9"
            mb={10}
            mt={10}
          />
        </Combobox.Dropdown>
      </Combobox>
      <Menu withArrow>
        <Menu.Target>
          <ThemeIcon c="optimaBlue.9" variant="white" size="sm">
            <IconInfoCircle />
          </ThemeIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Stack gap="xs">
            <Text size="sm" pl="xs" pr="xs">
              All searches require at least 3 characters to initiate a search.
            </Text>
            <Text size="sm" pl="xs" pr="xs">
              When searching by phone number, do not use any hyphens '-'.
            </Text>
            <Text size="sm" pl="xs" pr="xs">
              When searching by birth date, use the format YYYY-MM-DD.
            </Text>
          </Stack>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
